import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import type {
  BilanPilotage,
  CotePilotage,
  DormantePilotage,
  ExpositionPilotage,
  VentePilotage,
} from '@artosera/shared';
import { usePilotage, useGestePilotage } from '../../api/pilotage.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { CoutsExposition } from './CoutsExposition.js';
import styles from './Pilotage.module.css';
import { EtatPilotage, montantPilotage, useDatePilotage } from './presentation.js';

function Bilans({ bilans }: { bilans: BilanPilotage[] }) {
  const { t } = useTranslation('regie');
  return (
    <div>
      {bilans.map((b) => (
        <div key={b.devise} className={styles.bilan}>
          <strong>{b.devise}</strong>
          <span>
            {t('pilotage.ca')} : {montantPilotage(b.caHtCentimes, b.devise)}
          </span>
          {b.margeCentimes !== undefined && (
            <span>
              {t('pilotage.marge')} : {montantPilotage(b.margeCentimes, b.devise)}
            </span>
          )}
          <span>
            {t('pilotage.couts')} : {montantPilotage(b.coutsCentimes, b.devise)}
          </span>
          {b.resultatCentimes !== undefined && (
            <strong>
              {t('pilotage.resultat')} : {montantPilotage(b.resultatCentimes, b.devise)}
            </strong>
          )}
          {!!b.ventesSansPart && <p>{t('pilotage.nonCalculable', { count: b.ventesSansPart })}</p>}
        </div>
      ))}
    </div>
  );
}
export function VentesPilotage({ ventes }: { ventes: VentePilotage[] }) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation('regie');
  const date = useDatePilotage();
  const voirParts = ventes.some((v) => v.partArtisteCentimes !== undefined);
  function partAffichee(v: VentePilotage) {
    if (v.partArtisteCentimes === undefined) return '—';
    if (v.partArtisteCentimes === null) return t('pilotage.inconnu');
    return montantPilotage(v.partArtisteCentimes, v.devise);
  }
  return (
    <div className={styles.defilement}>
      <table>
        <thead>
          <tr>
            {[
              'piece',
              'oeuvre',
              'date',
              'ca',
              ...(voirParts ? ['part'] : []),
              'prixEffectif',
              'prixPublic',
              'remise',
              'technique',
              'format',
            ].map((c) => (
              <th key={c}>{t(`pilotage.${c}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ventes.map((v) => (
            <tr key={v.id}>
              <td>
                <Link
                  to={`/${galerieSlug}/${v.nature === 'facture' ? 'archive-factures' : 'oeuvres'}/${v.pieceId}`}
                >
                  {v.numero}
                </Link>
              </td>
              <td>
                <Link to={`/${galerieSlug}/oeuvres/${v.oeuvreId}`}>{v.titre}</Link>
              </td>
              <td>{date(v.date)}</td>
              <td>{montantPilotage(v.caHtCentimes, v.devise)}</td>
              {voirParts && <td>{partAffichee(v)}</td>}
              <td>{montantPilotage(v.prixHtCentimes, v.devise)}</td>
              <td>
                {v.prixPublicCentimes === null
                  ? t('pilotage.inconnu')
                  : montantPilotage(v.prixPublicCentimes, v.devise)}
              </td>
              <td>{v.remisePdb === null ? '—' : `${(v.remisePdb / 100).toFixed(2)} %`}</td>
              <td>{v.technique}</td>
              <td>{t(`pilotage.formats.${v.format}`)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!ventes.length && <p>{t('pilotage.vide')}</p>}
    </div>
  );
}
function Rentabilite() {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation('regie');
  const date = useDatePilotage();
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const query = new URLSearchParams({ ...(du ? { du } : {}), ...(au ? { au } : {}) });
  const liste = usePilotage<{ expositions: ExpositionPilotage[] }>(
    galerieSlug,
    `/expositions?${query}`,
  );
  return (
    <section>
      <div className={styles.filtres}>
        <label>
          {t('pilotage.du')}
          <Input type="date" value={du} onChange={(e) => setDu(e.target.value)} />
        </label>
        <label>
          {t('pilotage.au')}
          <Input type="date" value={au} onChange={(e) => setAu(e.target.value)} />
        </label>
      </div>
      <EtatPilotage enCours={liste.isPending} erreur={liste.error} />
      <div className={styles.defilement}>
        <table>
          <thead>
            <tr>
              <th>{t('pilotage.exposition')}</th>
              <th>{t('pilotage.horizon3')}</th>
              <th>{t('pilotage.horizon6')}</th>
            </tr>
          </thead>
          <tbody>
            {liste.data?.expositions.map((e) => (
              <tr key={e.id}>
                <td>
                  <Link to={`/${galerieSlug}/pilotage/expositions/${e.id}`}>{e.titre}</Link>
                  <p>
                    {date(e.debutLe)} — {date(e.finLe)}
                  </p>
                </td>
                {e.horizons.map((h) => (
                  <td key={h.mois}>
                    <Bilans bilans={h.bilans} />
                    {!h.complet && (
                      <small>{t('pilotage.provisoire', { date: date(h.observeJusquAu) })}</small>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {liste.data?.expositions.length === 0 && <p>{t('pilotage.vide')}</p>}
    </section>
  );
}
function Agregats({ lignes, titre }: { lignes: CotePilotage['parAnnee']; titre: string }) {
  const { t } = useTranslation('regie');
  return (
    <section>
      <h3>{titre}</h3>
      <div className={styles.defilement}>
        <table>
          <thead>
            <tr>
              {['groupe', 'nombre', 'median', 'medianM2', 'remise'].map((k) => (
                <th key={k}>{t(`pilotage.${k}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => (
              <tr key={`${l.valeur}:${l.devise}`}>
                <td>
                  {l.valeur} · {l.devise}
                </td>
                <td>{l.nombre}</td>
                <td>{montantPilotage(l.prixMedianCentimes, l.devise)}</td>
                <td>
                  {l.prixMedianM2Centimes === null
                    ? '—'
                    : montantPilotage(l.prixMedianM2Centimes, l.devise)}
                </td>
                <td>
                  {l.remiseMoyennePdb === null ? '—' : `${(l.remiseMoyennePdb / 100).toFixed(2)} %`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function CoteArtiste() {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation('regie');
  const [artiste, setArtiste] = useState('');
  const artistes = usePilotage<{ artistes: { id: string; nom: string }[] }>(
    galerieSlug,
    '/artistes',
  );
  const cote = usePilotage<CotePilotage>(galerieSlug, `/artistes/${artiste}/cote`, !!artiste);
  return (
    <section>
      <label>
        {t('pilotage.artiste')}
        <select value={artiste} onChange={(e) => setArtiste(e.target.value)}>
          <option value="">{t('pilotage.choisir')}</option>
          {artistes.data?.artistes.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nom}
            </option>
          ))}
        </select>
      </label>
      <EtatPilotage
        enCours={artistes.isPending || (!!artiste && cote.isPending)}
        erreur={artistes.error ?? cote.error}
      />
      {cote.data && (
        <>
          {[...new Set(cote.data.parAnnee.map((a) => a.devise))].map((devise) => {
            const valeurs = cote.data.parAnnee.filter((a) => a.devise === devise);
            const maximum = Math.max(1, ...valeurs.map((a) => a.prixMedianCentimes));
            return (
              <figure key={devise}>
                <figcaption>
                  {t('pilotage.evolution')} · {devise}
                </figcaption>
                <svg
                  viewBox="0 0 600 180"
                  role="img"
                  aria-label={`${t('pilotage.evolution')} ${devise}`}
                >
                  <polyline
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    points={valeurs
                      .map(
                        (a, i) =>
                          `${35 + (i * 530) / Math.max(1, valeurs.length - 1)},${140 - (a.prixMedianCentimes / maximum) * 110}`,
                      )
                      .join(' ')}
                  />
                  {valeurs.map((a, i) => (
                    <g key={a.valeur}>
                      <circle
                        cx={35 + (i * 530) / Math.max(1, valeurs.length - 1)}
                        cy={140 - (a.prixMedianCentimes / maximum) * 110}
                        r="4"
                      />
                      <text
                        x={35 + (i * 530) / Math.max(1, valeurs.length - 1)}
                        y="165"
                        textAnchor="middle"
                      >
                        {a.valeur}
                      </text>
                    </g>
                  ))}
                </svg>
              </figure>
            );
          })}
          <Agregats titre={t('pilotage.parAnnee')} lignes={cote.data.parAnnee} />
          <Agregats
            titre={t('pilotage.parFormat')}
            lignes={cote.data.parFormat.map((l) => ({
              ...l,
              valeur: t(`pilotage.formats.${l.valeur}`),
            }))}
          />
          <Agregats titre={t('pilotage.parTechnique')} lignes={cote.data.parTechnique} />
          <VentesPilotage ventes={cote.data.ventes} />
        </>
      )}
    </section>
  );
}
function Dormantes() {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const date = useDatePilotage();
  const [artiste, setArtiste] = useState('');
  const [anciennete, setAnciennete] = useState('');
  const [seuil, setSeuil] = useState('');
  const liste = usePilotage<{ moisDormance: number; oeuvres: DormantePilotage[] }>(
    galerieSlug,
    '/dormantes',
  );
  const geste = useGestePilotage(galerieSlug);
  const artistes = new Map(liste.data?.oeuvres.map((o) => [o.artisteId, o.artisteNom]));
  const destinations = {
    salle_privee: 'viewing-rooms',
    exposer: 'expositions',
    revoir_prix: 'oeuvres',
    rendre: 'mouvements',
  };
  return (
    <section>
      <div className={styles.filtres}>
        <label>
          {t('pilotage.artiste')}
          <select value={artiste} onChange={(e) => setArtiste(e.target.value)}>
            <option value="">{t('pilotage.tous')}</option>
            {[...artistes].map(([id, nom]) => (
              <option key={id} value={id}>
                {nom}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('pilotage.anciennete')}
          <Input
            type="number"
            min="0"
            value={anciennete}
            onChange={(e) => setAnciennete(e.target.value)}
          />
        </label>
        {galerieALaction(galerie, 'insights:write') && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              geste.mutate({
                methode: 'put',
                suite: '/reglages',
                corps: { moisDormance: Number(seuil) },
              });
            }}
          >
            <label>
              {t('pilotage.seuil')}
              <Input
                type="number"
                min="3"
                max="36"
                required
                value={seuil}
                placeholder={String(liste.data?.moisDormance ?? 12)}
                onChange={(e) => setSeuil(e.target.value)}
              />
            </label>
            <Button type="submit" disabled={geste.isPending}>
              {t('pilotage.enregistrer')}
            </Button>
          </form>
        )}
      </div>
      <EtatPilotage
        enCours={liste.isPending || geste.isPending}
        erreur={liste.error ?? geste.error}
      />
      {geste.isSuccess && <p role="status">{t('pilotage.enregistre')}</p>}
      <div className={styles.defilement}>
        <table>
          <thead>
            <tr>
              {['oeuvre', 'artiste', 'derniereActivite', 'decision'].map((k) => (
                <th key={k}>{t(`pilotage.${k}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {liste.data?.oeuvres
              .filter(
                (o) =>
                  (!artiste || o.artisteId === artiste) && o.ancienneteMois >= Number(anciennete),
              )
              .map((o) => {
                let destination = '';
                if (o.decision) destination = `/${galerieSlug}/${destinations[o.decision]}`;
                if (o.decision === 'revoir_prix') destination += `/${o.id}`;
                const moduleRequis = {
                  salle_privee: 'viewing-rooms',
                  exposer: 'expositions',
                  revoir_prix: 'oeuvres',
                  rendre: 'mouvements',
                } as const;
                const accessible = o.decision && galerieAModule(galerie, moduleRequis[o.decision]);
                return (
                  <tr key={o.id}>
                    <td>
                      <Link to={`/${galerieSlug}/oeuvres/${o.id}`}>{o.titre}</Link>
                    </td>
                    <td>{o.artisteNom}</td>
                    <td>
                      {date(o.derniereActiviteLe)} · {o.ancienneteMois} {t('pilotage.mois')}
                    </td>
                    <td>
                      {accessible && (
                        <Link to={destination}>{t(`pilotage.decisions.${o.decision}`)}</Link>
                      )}
                      {!accessible && o.decision && t(`pilotage.decisions.${o.decision}`)}
                      {!o.decision && '—'}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {liste.data?.oeuvres.length === 0 && <p>{t('pilotage.vide')}</p>}
    </section>
  );
}
export function PilotagePage() {
  const { galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const direction = galerieALaction(galerie, 'insights:read');
  const [parametres] = useSearchParams();
  const [volet, setVolet] = useState(
    direction && parametres.get('volet') !== 'dormantes' ? 'rentabilite' : 'dormantes',
  );
  const voletAffiche = direction ? volet : 'dormantes';
  return (
    <div className={styles.page} data-ui="page">
      <h1>{t('pilotage.titre')}</h1>
      <nav className={styles.filtres} aria-label={t('pilotage.titre')}>
        {(direction ? ['rentabilite', 'cote', 'dormantes'] : ['dormantes']).map((v) => (
          <Button key={v} aria-pressed={voletAffiche === v} onClick={() => setVolet(v)}>
            {t(`pilotage.${v}`)}
          </Button>
        ))}
      </nav>
      {direction && voletAffiche === 'rentabilite' && <Rentabilite />}
      {direction && voletAffiche === 'cote' && <CoteArtiste />}
      {voletAffiche === 'dormantes' && <Dormantes />}
    </div>
  );
}
export function PilotageExpositionPage() {
  const { id = '' } = useParams();
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation('regie');
  const date = useDatePilotage();
  const fiche = usePilotage<{ exposition: ExpositionPilotage }>(galerieSlug, `/expositions/${id}`);
  return (
    <div className={styles.page} data-ui="page">
      <Link to={`/${galerieSlug}/pilotage`}>{t('pilotage.titre')}</Link>
      <EtatPilotage enCours={fiche.isPending} erreur={fiche.error} />
      {fiche.data && (
        <>
          <h1>{fiche.data.exposition.titre}</h1>
          <Link to={`/${galerieSlug}/expositions/${id}`}>{t('pilotage.exposition')}</Link>
          {fiche.data.exposition.horizons.map((h) => (
            <section key={h.mois}>
              <h2>
                +{h.mois} {t('pilotage.mois')}
              </h2>
              {!h.complet && <p>{t('pilotage.provisoire', { date: date(h.observeJusquAu) })}</p>}
              <Bilans bilans={h.bilans} />
              <VentesPilotage ventes={h.ventes} />
            </section>
          ))}
          <CoutsExposition key={`${galerieSlug}:${id}`} expositionId={id} />
        </>
      )}
    </div>
  );
}
